import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, X, MapPin, Users, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';

const TournamentsCalendar = ({ tournaments }) => {
  const { isDarkMode } = useTheme();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get first and last day of the month
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  const startingDayOfWeek = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();

  // Get tournaments for the current month
  const tournamentsInMonth = tournaments.filter(tournament => {
    const tournamentDate = new Date(tournament.start_date);
    return (
      tournamentDate.getMonth() === currentDate.getMonth() &&
      tournamentDate.getFullYear() === currentDate.getFullYear()
    );
  });

  // Create a map of tournaments by day
  const tournamentsByDay = tournamentsInMonth.reduce((acc, tournament) => {
    const day = new Date(tournament.start_date).getDate();
    if (!acc[day]) acc[day] = [];
    acc[day].push(tournament);
    return acc;
  }, {});

  // Navigation
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get month/year display
  const monthYear = currentDate.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });

  // Days of week
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Generate calendar days
  const calendarDays = [];
  
  // Add empty cells for days before month starts
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }
  
  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  // Check if a day is today
  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const getCategoryColor = (category) => {
    const categoryLower = category?.toLowerCase();
    const colors = {
      'mens_open': 'bg-blue-500',
      'mens_mid_am': 'bg-blue-600',
      'mens_senior': 'bg-blue-700',
      'mens_super_senior': 'bg-orange-500',
      'ladies_open': 'bg-pink-500',
      'ladies_mid_am': 'bg-pink-600',
      'ladies_senior': 'bg-pink-700',
      'ladies_super_senior': 'bg-orange-600',
      'junior_boys': 'bg-green-500',
      'junior_girls': 'bg-green-600',
      'mixed': 'bg-purple-500',
      'country_districts': 'bg-teal-500',
    };
    return colors[categoryLower] || 'bg-fsgu-green-500';
  };

  const formatCategoryLabel = (category) => {
    const labels = {
      'Mens_Open': "Men's Open",
      'Mens_Mid_Am': "Men's Mid-Am",
      'Mens_Senior': "Men's Senior",
      'Mens_Super_Senior': "Men's Super Senior",
      'Ladies_Open': "Ladies Open",
      'Ladies_Mid_Am': "Ladies Mid-Am",
      'Ladies_Senior': "Ladies Senior",
      'Ladies_Super_Senior': "Ladies Super Senior",
      'Junior_Boys': "Junior Boys",
      'Junior_Girls': "Junior Girls",
      'Mixed': "Mixed",
      'Country_Districts': "Country Districts"
    };
    return labels[category] || category?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l.toUpperCase()) || 'Open';
  };

  const handleEventClick = (e, tournament) => {
    e.preventDefault();
    setSelectedEvent(tournament);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedEvent(null), 300);
  };

  const formatEventDate = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start.toDateString() === end.toDateString()) {
      return start.toLocaleDateString('en-ZA', { 
        weekday: 'long',
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    }
    
    return `${start.toLocaleDateString('en-ZA', { 
      day: 'numeric', 
      month: 'short' 
    })} - ${end.toLocaleDateString('en-ZA', { 
      day: 'numeric', 
      month: 'long',
      year: 'numeric' 
    })}`;
  };

  return (
    <div className={`rounded-2xl border ${
      isDarkMode
        ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
        : 'bg-fsgu-white border-fsgu-silver-200'
    }`}>
      {/* Calendar Header */}
      <div className="p-4 sm:p-6 border-b border-inherit">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <h2 className={`text-xl sm:text-2xl font-bold ${
            isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
          }`}>
            {monthYear}
          </h2>

          <div className="flex items-center space-x-2">
            <button
              onClick={goToToday}
              className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                isDarkMode
                  ? 'bg-fsgu-gray-700 hover:bg-fsgu-gray-600 text-fsgu-silver-300'
                  : 'bg-fsgu-silver-100 hover:bg-fsgu-silver-200 text-fsgu-silver-700'
              }`}
            >
              Today
            </button>

            <button
              onClick={goToPreviousMonth}
              className={`p-2 rounded-lg transition-all ${
                isDarkMode
                  ? 'bg-fsgu-gray-700 hover:bg-fsgu-gray-600 text-fsgu-silver-300'
                  : 'bg-fsgu-silver-100 hover:bg-fsgu-silver-200 text-fsgu-silver-700'
              }`}
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>

            <button
              onClick={goToNextMonth}
              className={`p-2 rounded-lg transition-all ${
                isDarkMode
                  ? 'bg-fsgu-gray-700 hover:bg-fsgu-gray-600 text-fsgu-silver-300'
                  : 'bg-fsgu-silver-100 hover:bg-fsgu-silver-200 text-fsgu-silver-700'
              }`}
            >
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-3 sm:p-6">
        {/* Days of Week Header */}
        <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
          {daysOfWeek.map((day) => (
            <div
              key={day}
              className={`text-center text-xs sm:text-sm font-medium py-1 sm:py-2 ${
                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
              }`}
            >
              <span className="hidden sm:inline">{day}</span>
              <span className="sm:hidden">{day.charAt(0)}</span>
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {calendarDays.map((day, index) => (
            <div
              key={index}
              className={`min-h-16 sm:min-h-24 p-1 sm:p-2 rounded-lg border ${
                day === null
                  ? 'border-transparent'
                  : isToday(day)
                  ? isDarkMode
                    ? 'border-fsgu-green-500 bg-fsgu-green-500/10'
                    : 'border-fsgu-green-500 bg-fsgu-green-500/10'
                  : isDarkMode
                  ? 'border-fsgu-gray-700 hover:border-fsgu-gray-600'
                  : 'border-fsgu-silver-200 hover:border-fsgu-silver-300'
              }`}
            >
              {day && (
                <>
                  {/* Day Number */}
                  <div className={`text-xs sm:text-sm font-medium mb-0.5 sm:mb-1 ${
                    isToday(day)
                      ? isDarkMode
                        ? 'text-fsgu-green-400'
                        : 'text-fsgu-green-600'
                      : isDarkMode
                      ? 'text-fsgu-silver-300'
                      : 'text-fsgu-silver-700'
                  }`}>
                    {day}
                  </div>

                  {/* Tournament Dots */}
                  {tournamentsByDay[day] && (
                    <div className="space-y-0.5 sm:space-y-1">
                      {tournamentsByDay[day].slice(0, 2).map((tournament, idx) => (
                        <button
                          key={idx}
                          onClick={(e) => handleEventClick(e, tournament)}
                          className={`hidden sm:block w-full text-left text-xs px-1.5 py-0.5 rounded truncate ${
                            getCategoryColor(tournament.category)
                          } text-white hover:opacity-80 transition-opacity`}
                          title={tournament.tournament_name}
                        >
                          {tournament.tournament_name}
                        </button>
                      ))}
                      {/* Mobile: Show dots instead of text */}
                      <div className="sm:hidden flex flex-wrap gap-0.5">
                        {tournamentsByDay[day].slice(0, 3).map((tournament, idx) => (
                          <button
                            key={idx}
                            onClick={(e) => handleEventClick(e, tournament)}
                            className={`w-1.5 h-1.5 rounded-full ${
                              getCategoryColor(tournament.category)
                            }`}
                            title={tournament.tournament_name}
                          />
                        ))}
                      </div>
                      {tournamentsByDay[day].length > 2 && (
                        <div className={`hidden sm:block text-xs px-1.5 ${
                          isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                        }`}>
                          +{tournamentsByDay[day].length - 2} more
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className={`p-4 sm:p-6 border-t ${
        isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
      }`}>
        <div className="flex flex-wrap gap-3 sm:gap-4 justify-center sm:justify-start">
          <div className="flex items-center space-x-1.5 sm:space-x-2">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-blue-500"></div>
            <span className={`text-xs ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>Men's</span>
          </div>
          <div className="flex items-center space-x-1.5 sm:space-x-2">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-pink-500"></div>
            <span className={`text-xs ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>Ladies</span>
          </div>
          <div className="flex items-center space-x-1.5 sm:space-x-2">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-500"></div>
            <span className={`text-xs ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>Juniors</span>
          </div>
          <div className="flex items-center space-x-1.5 sm:space-x-2">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-purple-500"></div>
            <span className={`text-xs ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>Mixed</span>
          </div>
          <div className="flex items-center space-x-1.5 sm:space-x-2">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-orange-500"></div>
            <span className={`text-xs ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>Super Senior</span>
          </div>
          <div className="flex items-center space-x-1.5 sm:space-x-2">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-teal-500"></div>
            <span className={`text-xs ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>Country Districts</span>
          </div>
        </div>
      </div>

      {/* Event Modal */}
      {isModalOpen && selectedEvent && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={closeModal}
        >
          <div 
            className={`relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border border-fsgu-gray-700'
                : 'bg-fsgu-white border border-fsgu-silver-200'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeModal}
              className={`absolute top-4 right-4 p-2 rounded-lg transition-colors z-10 ${
                isDarkMode
                  ? 'hover:bg-fsgu-gray-700 text-fsgu-silver-400'
                  : 'hover:bg-fsgu-silver-100 text-fsgu-silver-600'
              }`}
            >
              <X className="w-5 h-5" />
            </button>

            {/* Event Image */}
            {selectedEvent.image_url && (
              <div className="aspect-video w-full overflow-hidden rounded-t-2xl">
                <img
                  src={selectedEvent.image_url}
                  alt={selectedEvent.tournament_name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Event Content */}
            <div className="p-6 sm:p-8">
              {/* Category Badge */}
              {selectedEvent.category && (
                <div className="mb-4">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                    getCategoryColor(selectedEvent.category)
                  } text-white`}>
                    {formatCategoryLabel(selectedEvent.category)}
                  </span>
                </div>
              )}

              {/* Event Title */}
              <h2 className={`text-2xl sm:text-3xl font-bold mb-4 ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                {selectedEvent.tournament_name}
              </h2>

              {/* Event Details */}
              <div className="space-y-4 mb-6">
                {/* Date */}
                <div className="flex items-start space-x-3">
                  <CalendarIcon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                    isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                  }`} />
                  <div>
                    <p className={`text-sm font-medium ${
                      isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                    }`}>
                      {formatEventDate(selectedEvent.start_date, selectedEvent.end_date)}
                    </p>
                  </div>
                </div>

                {/* Location */}
                {selectedEvent.venue_name && (
                  <div className="flex items-start space-x-3">
                    <MapPin className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                    }`} />
                    <div>
                      <p className={`text-sm font-medium ${
                        isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                      }`}>
                        {selectedEvent.venue_name}
                      </p>
                      {selectedEvent.venue_city && (
                        <p className={`text-sm ${
                          isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                        }`}>
                          {selectedEvent.venue_city}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Max Participants */}
                {selectedEvent.max_participants && (
                  <div className="flex items-start space-x-3">
                    <Users className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                    }`} />
                    <p className={`text-sm ${
                      isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                    }`}>
                      Maximum {selectedEvent.max_participants} participants
                    </p>
                  </div>
                )}
              </div>

              {/* Description */}
              {selectedEvent.description && (
                <div className="mb-6">
                  <p className={`text-sm leading-relaxed ${
                    isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                  }`}>
                    {selectedEvent.description}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to={`/tournaments/${selectedEvent.id}`}
                  className="btn btn-primary flex-1 inline-flex items-center justify-center"
                  onClick={closeModal}
                >
                  View Full Details
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Link>
                {selectedEvent.status === 'registration_open' && (
                  <Link
                    to={`/tournaments/${selectedEvent.id}/register`}
                    className={`flex-1 inline-flex items-center justify-center px-6 py-3 rounded-lg font-medium transition-all ${
                      isDarkMode
                        ? 'bg-fsgu-green-500/20 text-fsgu-green-400 hover:bg-fsgu-green-500/30 border border-fsgu-green-500/30'
                        : 'bg-fsgu-green-500/10 text-fsgu-green-600 hover:bg-fsgu-green-500/20 border border-fsgu-green-500/30'
                    }`}
                    onClick={closeModal}
                  >
                    Register Now
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentsCalendar;